/**
 * useColumnPersistence
 *
 * Schema-driven column preference persistence for DynamicTableV2.
 * When `persistence.enabled` is true in the schema, this hook:
 *   - Fetches saved column preferences from the API on mount
 *   - Saves preferences back via PUT when columns change
 *   - Exposes `selectedColumns` (the active column list) and `saveColumns`
 *     (the save handler) to the table
 *
 * API contract (matches wealthlane-award-services TablePreferencesService):
 *   GET  /api/award/api/app/table-preferences/table-preferences?tableName={key}
 *        → string[]   (list of visible column field names)
 *   PUT  /api/award/api/app/table-preferences/table-preferences
 *        body: { tableName: string; preferences: string[] }
 *        → boolean
 *
 * No external props needed — the table drives this entirely from its schema.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

type AxiosInstance = {
  get: Function;
  post: Function;
  put: Function;
  delete: Function;
  request: Function;
};

interface UseColumnPersistenceOptions {
  enabled: boolean;
  tableKey: string;
  axiosInstance?: AxiosInstance;
}

interface UseColumnPersistenceResult {
  /** The persisted column list, or undefined while loading / when persistence is off */
  selectedColumns: string[] | undefined;
  /** Call this with the new column list to save it */
  saveColumns: (columns: string[]) => void;
  /** True while the initial fetch or a save is in flight */
  loading: boolean;
}

const GET_PATH = '/api/award/api/app/table-preferences/table-preferences';
const PUT_PATH = '/api/award/api/app/table-preferences/table-preferences';

export function useColumnPersistence({
  enabled,
  tableKey,
  axiosInstance,
}: UseColumnPersistenceOptions): UseColumnPersistenceResult {
  const [selectedColumns, setSelectedColumns] = useState<string[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch on mount when persistence is enabled
  useEffect(() => {
    if (!enabled || !axiosInstance || !tableKey) return;

    setLoading(true);
    axiosInstance
      .get(GET_PATH, { params: { tableName: tableKey } })
      .then((response: any) => {
        if (!mountedRef.current) return;
        // API returns the data directly or inside a .data wrapper
        const data: string[] | undefined =
          Array.isArray(response) ? response :
          Array.isArray(response?.data) ? response.data :
          undefined;
        setSelectedColumns(data && data.length > 0 ? data : undefined);
      })
      .catch(() => {
        // Silently fall back to schema defaults on error
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tableKey]);

  const saveColumns = useCallback(
    (columns: string[]) => {
      if (!enabled || !axiosInstance || !tableKey) return;
      // Optimistically update local state immediately
      setSelectedColumns(columns);
      setLoading(true);
      axiosInstance
        .put(PUT_PATH, { tableName: tableKey, preferences: columns })
        .catch(() => {
          // Revert isn't critical — column visibility is cosmetic
        })
        .finally(() => {
          if (mountedRef.current) setLoading(false);
        });
    },
    [enabled, tableKey, axiosInstance]
  );

  if (!enabled) {
    return { selectedColumns: undefined, saveColumns: () => {}, loading: false };
  }

  return { selectedColumns, saveColumns, loading };
}
